import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/App';
import apiClient from '@/utils/api';
import { EXCEPTION_ROLES } from '@/utils/navigation';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { ListChecks, BarChart3, AlertTriangle, CalendarDays } from 'lucide-react';
import MySessions from './tasks/MySessions';
import TaskQueue from './tasks/TaskQueue';
import MyDailyReport from './tasks/MyDailyReport';
import AdminDailyReport from './tasks/AdminDailyReport';
import ExceptionQueue from './ExceptionQueue';

export default function TasksPage() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const manager = EXCEPTION_ROLES.includes(user?.role);
  const canCounsel = ['counselor', 'team_leader'].includes(user?.role);
  const personalReport = ['telecaller', 'counselor'].includes(user?.role);
  const showReports = manager || personalReport;
  const [reviewCount, setReviewCount] = useState(0);
  useEffect(() => {
    if (!manager) return;
    let active = true;
    const load = async () => {
      try {
        const { data } = await apiClient.get('/workflow/exceptions?status=open');
        if (active) setReviewCount((data.exceptions || []).length);
      } catch { /* ignore */ }
    };
    load();
    const timer = setInterval(load, 60000);
    window.addEventListener('focus', load);
    return () => { active = false; clearInterval(timer); window.removeEventListener('focus', load); };
  }, [manager]);
  const tabs = [['queue', 'Tasks', ListChecks], ...(canCounsel ? [['sessions', 'My Sessions', CalendarDays]] : []), ...(showReports ? [['reports', 'Reports', BarChart3]] : []), ...(manager ? [['exceptions', 'Review', AlertTriangle]] : [])];
  const requested = params.get('view');
  const view = tabs.some(([key]) => key === requested) ? requested : 'queue';
  return <div className="tasks-workspace space-y-3 min-w-0" data-testid="tasks-workspace">
    <h1 className="text-2xl sm:text-3xl font-bold text-[#1B2559]" data-testid="tasks-heading">Tasks</h1>
    <Tabs value={view} onValueChange={value => setParams(value === 'queue' ? {} : { view: value })}>
      {tabs.length > 1 && <TabsList className={`flex flex-wrap h-auto w-fit max-w-full ${tabs.length > 3 ? 'tasks-views-many' : ''}`} data-testid="tasks-views">
        {tabs.map(([key, label, Icon]) => <TabsTrigger key={key} value={key} className="min-h-[44px]" data-testid={`tasks-view-${key}`}><Icon className="w-4 h-4 mr-1.5" />{label}{key === 'exceptions' && reviewCount > 0 && <span className="ml-1.5 inline-flex items-center justify-center rounded-full bg-red-500 px-1.5 min-w-[20px] h-5 text-[11px] font-bold text-white" data-testid="review-count-badge">{reviewCount > 99 ? '99+' : reviewCount}</span>}</TabsTrigger>)}
      </TabsList>}
      <TabsContent value="queue"><TaskQueue key={user.id + user.role} /></TabsContent>
      {canCounsel && <TabsContent value="sessions"><MySessions key={user.id + user.role} /></TabsContent>}
      {showReports && <TabsContent value="reports">{manager ? <AdminDailyReport /> : <MyDailyReport />}</TabsContent>}
      {manager && <TabsContent value="exceptions"><ExceptionQueue /></TabsContent>}
    </Tabs>
  </div>;
}